import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { DataGrid } from '@mui/x-data-grid'
import { useNavigate } from 'react-router-dom'
import API from '../../services/API'
import BigModal from '../../components/Modals/BigModal'
import Impersonate from '../Tenants/Impersonate'
import SupportRequestForm from '../../pages/dev/SupportRequestForm'

const MIN_QUERY_LENGTH = 3

const SearchUsers = () => {
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState(null)
  const [truncated, setTruncated] = useState(false)
  const [failedTenants, setFailedTenants] = useState([])
  const [impersonateUser, setImpersonateUser] = useState(null)
  const [supportUser, setSupportUser] = useState(null)
  const navigate = useNavigate()

  const handleSearch = (e) => {
    e.preventDefault()
    if (query.trim().length < MIN_QUERY_LENGTH || loading) return
    setLoading(true)
    API.get('/admincrm/users/search?q=' + encodeURIComponent(query.trim()))
      .then((res) => {
        if (res.status === 200) {
          setResults(res.data.users)
          setTruncated(res.data.truncated)
          setFailedTenants(res.data.failedTenants || [])
        }
      })
      .finally(() => setLoading(false))
  }

  return (
    <Stack gap={1}>
      <Typography variant="title2">Search Users</Typography>
      <Stack
        component="form"
        onSubmit={handleSearch}
        direction="row"
        gap={1}
        alignItems="center"
      >
        <TextField
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name or email across all tenants"
          size="small"
          fullWidth
          autoFocus
        />
        <Button
          type="submit"
          variant="contained"
          startIcon={<SearchIcon />}
          disabled={query.trim().length < MIN_QUERY_LENGTH || loading}
        >
          {loading ? 'Searching...' : 'Search'}
        </Button>
      </Stack>
      {truncated && (
        <Alert severity="warning">
          Too many matches - only the first results are shown. Try a more
          specific search.
        </Alert>
      )}
      {failedTenants.length > 0 && (
        <Alert severity="error">
          Could not search these tenants: {failedTenants.join(', ')}
        </Alert>
      )}
      {results === null ? (
        <Typography color="text.secondary" mt={2}>
          Enter at least {MIN_QUERY_LENGTH} characters and search to find a
          user in any tenant.
        </Typography>
      ) : (
        <DataGrid
          rows={results}
          loading={loading}
          getRowId={(row) => `${row.tenant._id}-${row._id}`}
          disableRowSelectionOnClick
          localeText={{ noRowsLabel: 'No users matched your search' }}
          columns={[
            {
              field: 'name',
              headerName: 'Name',
              flex: 1,
              minWidth: 150,
              valueGetter: (value, row) =>
                `${row.firstName || ''} ${row.lastName || ''}`.trim(),
              renderCell: ({ row }) => (
                <Stack direction="row" gap={1} alignItems="center">
                  <Box>
                    {row.firstName} {row.lastName}
                  </Box>
                  {row.security?.isTenantOwner && (
                    <Chip label="Owner" size="small" color="anPurple" />
                  )}
                  {row.security?.isSiteAdmin && (
                    <Chip label="Admin" size="small" color="anBlue" />
                  )}
                </Stack>
              ),
            },
            { field: 'email', headerName: 'Email', flex: 1, minWidth: 180 },
            {
              field: 'tenant',
              headerName: 'Tenant',
              flex: 1,
              minWidth: 150,
              valueGetter: (value, row) => row.tenant?.name,
              renderCell: ({ row }) => (
                <Stack direction="row" gap={1} alignItems="center">
                  <Box>{row.tenant?.name}</Box>
                  <Chip
                    label={row.tenant?.subdomain}
                    size="small"
                    variant="outlined"
                  />
                </Stack>
              ),
            },
            {
              field: 'actions',
              headerName: '',
              flex: 1.2,
              minWidth: 320,
              sortable: false,
              renderCell: ({ row }) => (
                <Stack direction="row" gap={1} justifyContent="center">
                  <Button
                    color="anBlue"
                    onClick={() => setImpersonateUser(row)}
                  >
                    Log In As
                  </Button>
                  <Button color="primary" onClick={() => setSupportUser(row)}>
                    Add Request
                  </Button>
                  <Button
                    color="third"
                    onClick={() => navigate(`/tenants/${row.tenant._id}`)}
                  >
                    View Tenant
                  </Button>
                </Stack>
              ),
            },
          ]}
          sx={{
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
            },
            '& .MuiDataGrid-sortIcon': {
              opacity: 1,
              color: 'primary.contrastText',
            },
            '& .MuiDataGrid-menuIconButton': {
              opacity: 1,
              color: 'primary.contrastText',
            },
            '.MuiDataGrid-cell:focus': {
              outline: 'none',
            },
          }}
        />
      )}
      <BigModal
        open={!!impersonateUser}
        onClose={() => setImpersonateUser(null)}
      >
        <Impersonate
          tenantId={impersonateUser?.tenant?._id}
          subdomain={impersonateUser?.tenant?.subdomain}
          userId={impersonateUser?._id}
        />
      </BigModal>
      <BigModal
        open={!!supportUser}
        onClose={() => setSupportUser(null)}
        style={{
          p: 0,
          borderRadius: 4,
          overflow: 'hidden',
          width: '95%',
        }}
      >
        <SupportRequestForm
          adminMode
          tenantId={supportUser?.tenant?._id}
          subdomain={supportUser?.tenant?.subdomain}
          userId={supportUser?._id}
          userName={supportUser?.firstName}
          userEmail={supportUser?.email}
          onClose={() => setSupportUser(null)}
        />
      </BigModal>
    </Stack>
  )
}

export default SearchUsers
